import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import {
  Box,
  IconButton,
  Popover,
  Stack,
  Switch,
  FormControlLabel,
  Tooltip,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Slider,
  useTheme,
} from '@mui/material'
import TranslateIcon from '@mui/icons-material/Translate'
import HeadphonesIcon from '@mui/icons-material/Headphones'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import FormatSizeIcon from '@mui/icons-material/FormatSize'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import CheckIcon from '@mui/icons-material/Check'
import { useTranslation } from 'react-i18next'
import { RECITERS } from '../lib/reciters'
import { languageLabel, sortEditions } from '../lib/editions'
import type { Edition } from '../types'

type ArabicFont = 'kfgqpc' | 'uthmani' | 'indopak' | 'simple'
type ViewMode = 'verses' | 'book'
type PanelKey = 'translation' | 'reciter' | 'arabicFont' | 'fontSize'

interface ReaderFloatingSettingsProps {
  edition: string
  availableEditions: Edition[]
  reciter: string
  arabicFont: ArabicFont
  fontSize: number
  wbwEnabled: boolean
  viewMode: ViewMode
  onEditionChange: (v: string) => void
  onReciterChange: (v: string) => void
  onArabicFontChange: (v: ArabicFont) => void
  onFontSizeChange: (v: number) => void
  onWbwToggle: () => void
  onViewModeChange: (m: ViewMode) => void
  /** 'rail' (default) = fixed right-edge column on mobile.
   *  'row' = horizontal strip mounted inside the sticky reader header on
   *  md+ so settings stay fixed while verses scroll. Purely a wrapper/
   *  trigger-styling switch — the popover contents are shared. */
  variant?: 'rail' | 'row'
}

const ARABIC_FONT_OPTIONS: { value: ArabicFont; labelKey: string }[] = [
  { value: 'kfgqpc', labelKey: 'reader.fontKfgqpc' },
  { value: 'uthmani', labelKey: 'reader.fontUthmani' },
  { value: 'indopak', labelKey: 'reader.fontIndopak' },
  { value: 'simple', labelKey: 'reader.fontSimple' },
]

/**
 * A vertical stack of 5 small floating icon buttons docked to the right edge
 * of the content area. Each button either opens a scoped popover OR triggers
 * a single-click action (the View toggle).
 *
 * Items, top → bottom:
 *   1. View toggle (no popover; cycles `verses ↔ book` on each click)
 *   2. Translation (popover with a direct clickable list of editions)
 *   3. Reciter   (popover with a direct clickable list of reciters)
 *   4. Arabic font (popover with font-family chips + WBW switch)
 *   5. Font size (popover with the font-size slider)
 *
 * On mobile the rail tucks into the bottom-right corner so the AudioPlayer
 * (which dominates the bottom edge) and these icons don't fight for the same
 * pixels. We sit just above the player via a higher `bottom` and a z-index
 * below it so the player still wins overlap.
 */
export function ReaderFloatingSettings({
  edition,
  availableEditions,
  reciter,
  arabicFont,
  fontSize,
  wbwEnabled,
  viewMode,
  onEditionChange,
  onReciterChange,
  onArabicFontChange,
  onFontSizeChange,
  onWbwToggle,
  onViewModeChange,
  variant = 'rail',
}: ReaderFloatingSettingsProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const isRow = variant === 'row'

  // Single open key + anchor — keeps mutual exclusivity simple (only one
  // popover at a time) and avoids 4 parallel useState pairs.
  const [openKey, setOpenKey] = useState<PanelKey | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpen = useCallback((key: PanelKey, el: HTMLElement) => {
    setAnchorEl(el)
    setOpenKey(key)
  }, [])
  const handleClose = useCallback(() => {
    setOpenKey(null)
    setAnchorEl(null)
  }, [])

  // Auto-hide: after HIDE_DELAY_MS of inactivity the column fades to a faint
  // hint (opacity 0.25, slight slide right) so it doesn't compete with the
  // ayat for the user's attention while reading. Mouse-enter on the column
  // itself OR on the right-edge wake zone reveals it; opening a popover
  // pins it visible until the popover closes.
  const HIDE_DELAY_MS = 4_000
  const [hidden, setHidden] = useState(false)
  const hideTimerRef = useRef<number | null>(null)

  const scheduleHide = useCallback(() => {
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current)
    hideTimerRef.current = window.setTimeout(() => setHidden(true), HIDE_DELAY_MS)
  }, [])

  const reveal = useCallback(() => {
    setHidden(false)
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current)
    scheduleHide()
  }, [scheduleHide])

  // Kick off the initial hide timer once on mount; cleanup any pending
  // timer when the component unmounts (route change away from reader).
  useEffect(() => {
    scheduleHide()
    return () => {
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current)
    }
  }, [scheduleHide])

  // Pin visible while a popover is open; resume auto-hide on close.
  useEffect(() => {
    if (openKey !== null) {
      setHidden(false)
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current)
    } else {
      scheduleHide()
    }
  }, [openKey, scheduleHide])

  // Sort editions by language → englishName so the popover list groups
  // visually. Memoised so the sort isn't recomputed on every render.
  const sortedEditions = useMemo(() => sortEditions(availableEditions), [availableEditions])

  const iconButtonSx = {
    width: 44,
    height: 44,
    borderRadius: '50%',
    bgcolor: 'background.paper',
    color: 'primary.main',
    border: 1,
    borderColor: 'divider',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 12px rgba(0,0,0,0.45)'
      : '0 4px 12px rgba(15,118,110,0.12), 0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.18s cubic-bezier(0.4,0,0.2,1)',
    '&:hover': {
      transform: 'scale(1.08)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 6px 18px rgba(0,0,0,0.55)'
        : '0 6px 18px rgba(15,118,110,0.18), 0 2px 6px rgba(0,0,0,0.08)',
    },
  } as const

  // Row variant: flat toolbar buttons living inside the shared bordered
  // strip (that strip already supplies the chrome — border/bg/padding — so
  // individual buttons stay borderless/shadowless here, unlike the rail's
  // floating pills above). Per the brief, only the currently-open popover's
  // trigger is tinted primary; the rest use the default icon color.
  const rowIconButtonSx = (active: boolean) =>
    ({
      width: 36,
      height: 36,
      borderRadius: 1.5,
      color: active ? 'primary.main' : 'text.secondary',
      transition: 'color 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        bgcolor: 'action.hover',
      },
    }) as const

  // Cycle the view mode on each click. `viewMode='book'` → next click goes
  // back to 'verses'. The icon shown indicates the CURRENT mode (per the
  // brief: MenuBookIcon for book, ViewModuleIcon for verses).
  const handleViewToggle = () => {
    onViewModeChange(viewMode === 'verses' ? 'book' : 'verses')
  }
  const viewIcon = viewMode === 'book' ? <MenuBookIcon fontSize="small" /> : <ViewModuleIcon fontSize="small" />
  // Tooltip describes the action (what clicking will do), not the current
  // state — matches the user's mental model of "what happens if I click".
  const viewLabel = viewMode === 'book' ? t('reader.switchToVersesView') : t('reader.switchToBookView')

  return (
    <>
      {isRow ? (
        // Row variant — horizontal strip mounted inside sticky ReaderControls
        // on md+. Same triggers, same shared openKey/anchorEl state and
        // popovers below; only the container + button chrome differ. No
        // auto-hide/wake-zone here — it's part of sticky page chrome.
        <Stack
          direction="row"
          spacing={0.5}
          data-testid="reader-toolrow"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2.5,
            px: 0.75,
            py: 0.25,
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 1px 0 rgba(255,255,255,0.04)'
                : '0 1px 2px rgba(15,23,42,0.04)',
          }}
        >
          {/* 1. View mode toggle — no popover, single-click cycles. */}
          <Tooltip title={viewLabel} placement="bottom">
            <IconButton aria-label={viewLabel} onClick={handleViewToggle} sx={rowIconButtonSx(false)}>
              {viewIcon}
            </IconButton>
          </Tooltip>

          {/* 2. Translation — popover with a clickable list. */}
          <Tooltip title={t('reader.translationSettings')} placement="bottom">
            <IconButton
              aria-label={t('reader.translationSettings')}
              onClick={(e) => handleOpen('translation', e.currentTarget)}
              sx={rowIconButtonSx(openKey === 'translation')}
            >
              <TranslateIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* 3. Reciter — popover with a clickable list. */}
          <Tooltip title={t('reader.reciterSettings')} placement="bottom">
            <IconButton
              aria-label={t('reader.reciterSettings')}
              onClick={(e) => handleOpen('reciter', e.currentTarget)}
              sx={rowIconButtonSx(openKey === 'reciter')}
            >
              <HeadphonesIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* 4. Arabic font — popover with font-family chips + WBW switch. */}
          <Tooltip title={t('reader.arabicFontSettings')} placement="bottom">
            <IconButton
              aria-label={t('reader.arabicFontSettings')}
              onClick={(e) => handleOpen('arabicFont', e.currentTarget)}
              sx={rowIconButtonSx(openKey === 'arabicFont')}
            >
              <TextFieldsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* 5. Font size — popover with just the slider. */}
          <Tooltip title={t('reader.fontSizeSettings')} placement="bottom">
            <IconButton
              aria-label={t('reader.fontSizeSettings')}
              onClick={(e) => handleOpen('fontSize', e.currentTarget)}
              sx={rowIconButtonSx(openKey === 'fontSize')}
            >
              <FormatSizeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : (
        <>
          {/* Floating rail — fixed to the right edge of the viewport. On mobile
              we anchor near the bottom-right (just above the AudioPlayer) so the
              icons remain tappable without overlapping the central reading area
              on small screens. With 5 items the column is taller; spacing 0.75
              keeps the rail compact enough to fit on small phones above the
              audio player. */}
          {/* Right-edge wake zone — invisible touch/hover target along the
              right ~32px of the viewport that re-reveals the column once it has
              faded out. pointer-events flips off when visible so it never blocks
              clicks on actual page content. */}
          <Box
            onMouseEnter={reveal}
            onTouchStart={reveal}
            sx={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 32,
              zIndex: theme.zIndex.drawer - 1,
              pointerEvents: hidden ? 'auto' : 'none',
            }}
            aria-hidden
          />

          <Stack
            direction="column"
            spacing={0.75}
            onMouseEnter={reveal}
            onTouchStart={reveal}
            onFocus={reveal}
            sx={{
              position: 'fixed',
              right: { xs: 12, md: 16 },
              // Desktop: vertically centred. Mobile: stacked above the bottom nav
              // & audio player (audio sits at bottom: 64-72px on xs).
              top: { xs: 'auto', sm: '50%' },
              bottom: { xs: 140, sm: 'auto' },
              // Compose the auto-hide slide into the existing centering transform.
              // When hidden: slight right shift + low opacity = the column melts
              // into the page chrome but stays discoverable via the wake zone.
              transform: hidden
                ? { xs: 'translateX(12px)', sm: 'translate(12px, -50%)' }
                : { xs: 'none', sm: 'translateY(-50%)' },
              opacity: hidden ? 0.25 : 1,
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              // Above page content but below the AudioPlayer (drawer + 1) so the
              // player keeps z-priority on bottom-overlap.
              zIndex: theme.zIndex.drawer,
            }}
          >
            {/* 1. View mode toggle — no popover, single-click cycles. */}
            <Tooltip title={viewLabel} placement="left">
              <IconButton aria-label={viewLabel} onClick={handleViewToggle} sx={iconButtonSx}>
                {viewIcon}
              </IconButton>
            </Tooltip>

            {/* 2. Translation — popover with a clickable list. */}
            <Tooltip title={t('reader.translationSettings')} placement="left">
              <IconButton
                aria-label={t('reader.translationSettings')}
                onClick={(e) => handleOpen('translation', e.currentTarget)}
                sx={iconButtonSx}
              >
                <TranslateIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* 3. Reciter — popover with a clickable list. */}
            <Tooltip title={t('reader.reciterSettings')} placement="left">
              <IconButton
                aria-label={t('reader.reciterSettings')}
                onClick={(e) => handleOpen('reciter', e.currentTarget)}
                sx={iconButtonSx}
              >
                <HeadphonesIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* 4. Arabic font — popover with font-family chips + WBW switch. */}
            <Tooltip title={t('reader.arabicFontSettings')} placement="left">
              <IconButton
                aria-label={t('reader.arabicFontSettings')}
                onClick={(e) => handleOpen('arabicFont', e.currentTarget)}
                sx={iconButtonSx}
              >
                <TextFieldsIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* 5. Font size — popover with just the slider. */}
            <Tooltip title={t('reader.fontSizeSettings')} placement="left">
              <IconButton
                aria-label={t('reader.fontSizeSettings')}
                onClick={(e) => handleOpen('fontSize', e.currentTarget)}
                sx={iconButtonSx}
              >
                <FormatSizeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </>
      )}

      {/* Translation popover: direct list of editions, no nested Autocomplete.
          Click → applies + closes. Scrollable cap so big edition lists stay
          on-screen. */}
      <Popover
        open={openKey === 'translation'}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 'min(320px, 92vw)', mr: 1 } } }}
      >
        <Box sx={{ p: 1.5, pb: 0.5 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
            {t('reader.translation')}
          </Typography>
        </Box>
        <List dense sx={{ maxHeight: 360, overflowY: 'auto', py: 0 }}>
          {sortedEditions.map((ed) => {
            const selected = ed.identifier === edition
            const lang = languageLabel(ed.language)
            return (
              <ListItemButton
                key={ed.identifier}
                selected={selected}
                onClick={() => {
                  onEditionChange(ed.identifier)
                  handleClose()
                }}
              >
                <ListItemText
                  primary={ed.englishName}
                  secondary={`${lang} · ${ed.name}`}
                  slotProps={{
                    primary: { variant: 'body2', noWrap: true },
                    secondary: { variant: 'caption', noWrap: true },
                  }}
                />
                {selected && <CheckIcon fontSize="small" color="primary" />}
              </ListItemButton>
            )
          })}
          {sortedEditions.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {t('games.loadingShort')}
              </Typography>
            </Box>
          )}
        </List>
      </Popover>

      {/* Reciter popover: direct clickable list, same treatment as
          Translation. */}
      <Popover
        open={openKey === 'reciter'}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 'min(320px, 92vw)', mr: 1 } } }}
      >
        <Box sx={{ p: 1.5, pb: 0.5 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
            {t('reader.reciter')}
          </Typography>
        </Box>
        <List dense sx={{ maxHeight: 360, overflowY: 'auto', py: 0 }}>
          {RECITERS.map((r) => {
            const selected = r.identifier === reciter
            return (
              <ListItemButton
                key={r.identifier}
                selected={selected}
                onClick={() => {
                  onReciterChange(r.identifier)
                  handleClose()
                }}
              >
                <ListItemText
                  primary={r.name}
                  slotProps={{ primary: { variant: 'body2', noWrap: true } }}
                />
                {selected && <CheckIcon fontSize="small" color="primary" />}
              </ListItemButton>
            )
          })}
        </List>
      </Popover>

      {/* Arabic font popover: family chips + WBW switch. WBW lives here
          because it's a "how Arabic text looks" decision — same family as
          the font choice. Disabled in book mode (matches the old UX). */}
      <Popover
        open={openKey === 'arabicFont'}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 'min(320px, 92vw)', mr: 1 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            {t('reader.arabicFont')}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
            {ARABIC_FONT_OPTIONS.map((opt) => {
              const selected = arabicFont === opt.value
              return (
                <Chip
                  key={opt.value}
                  label={t(opt.labelKey)}
                  onClick={() => onArabicFontChange(opt.value)}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  size="small"
                />
              )
            })}
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={wbwEnabled && viewMode === 'verses'}
                onChange={onWbwToggle}
                disabled={viewMode !== 'verses'}
                size="small"
              />
            }
            label={
              <Typography
                variant="body2"
                color={viewMode !== 'verses' ? 'text.disabled' : 'text.primary'}
              >
                {t('reader.showWbw')}
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        </Box>
      </Popover>

      {/* Font size popover: just the slider, with the current px label and
          a few preset marks. Kept narrow so it doesn't dominate the screen
          on mobile. */}
      <Popover
        open={openKey === 'fontSize'}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 'min(300px, 92vw)', mr: 1 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            {t('reader.fontSizeSettings')}
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            {t('reader.fontSize', { size: fontSize })}
          </Typography>
          <Slider
            value={fontSize}
            min={20}
            max={114}
            step={2}
            onChange={(_, v) => onFontSizeChange(v as number)}
            valueLabelDisplay="auto"
            marks={[
              { value: 20, label: '20' },
              { value: 48, label: '48' },
              { value: 72, label: '72' },
              { value: 114, label: '114' },
            ]}
            // A bit of horizontal padding so the marks at the edges don't
            // get clipped by the popover.
            sx={{ mt: 1, mb: 1, mx: 0.5 }}
          />
        </Box>
      </Popover>
    </>
  )
}

